import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from 'corso-admin-ui';
import { CommandAdminApi } from 'corso-types';
import api from '~/api';

export function useProtectSets() {
  return useQuery({
    queryKey: ['shipProtectSets'],
    queryFn: () => api.shipProtectSets.list(),
  });
}

export function useProtectSetById(setId?: number) {
  return useQuery({
    queryKey: ['shipProtectSet', `${setId}`],
    queryFn: () => api.shipProtectSets.getDetailById(setId),
    enabled: !!setId,
  });
}

export function useProtectSetStores(setId?: number) {
  return useQuery({
    queryKey: ['shipProtectSet', `${setId}`, 'stores'],
    queryFn: () => api.shipProtectSets.listStores(setId),
    enabled: !!setId,
  });
}

type UnitInput = CommandAdminApi.RequestBody<
  '/ship-protect-set/:setId/unit',
  'post'
>;

function useInvalidateSet(setId: number) {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      queryKey: ['shipProtectSet', `${setId}`],
    });
}

export function useCreateUnit(setId: number) {
  const invalidate = useInvalidateSet(setId);
  const toast = useToast();
  return useMutation({
    mutationFn: (body: UnitInput) =>
      api.shipProtectSets.createUnit(setId, body),
    onSuccess: () => {
      toast.show('Created unit');
      return invalidate();
    },
    onError: () => toast.show('Failed to create unit'),
  });
}

export function useUpdateUnit(setId: number) {
  const invalidate = useInvalidateSet(setId);
  const toast = useToast();
  return useMutation({
    mutationFn: ({ unitId, body }: { unitId: number; body: UnitInput }) =>
      api.shipProtectSets.updateUnit(setId, unitId, body),
    onSuccess: () => {
      toast.show('Updated unit');
      return invalidate();
    },
    onError: () => toast.show('Failed to update unit'),
  });
}

export function useDeleteUnit(setId: number) {
  const invalidate = useInvalidateSet(setId);
  const toast = useToast();
  return useMutation({
    mutationFn: (unitId: number) =>
      api.shipProtectSets.deleteUnit(setId, unitId),
    onSuccess: () => {
      toast.show('Deleted unit');
      return invalidate();
    },
    onError: () => toast.show('Failed to delete unit'),
  });
}
